/**
 * components/workspace/PackSection.jsx — RC 1.49.7 Phase 4
 *
 * Live Application Pack section for the route-based Application
 * Workspace. Mirrors the wizard's `createPack` contract verbatim —
 * same endpoint, same duplicate-pack 409 handling, same canonical
 * `pack_snapshot` assembly.
 *
 * Hard rules (operator-locked, RC 1.49.x + RC 1.49.7):
 *
 *   • Operator #3 — User MAY create a standard Application Pack
 *     even when Analyse Fit was skipped. The Pack creation is
 *     never gated on Fit presence.
 *
 *   • Operator #4 + #5 — Interview Pack content remains
 *     UNAVAILABLE unless an Analyse Fit row exists for the current
 *     workflow source. The exact operator-locked copy
 *
 *       "Interview Pack unavailable until Analyse Fit is
 *        completed for this application workflow."
 *
 *     is rendered verbatim when Fit is missing for the bound CV.
 *     This mirrors the wizard's RC 1.49.5A P0-A2 surface.
 *
 *   • Operator #6 + #7 — duplicate-pack 409 guard. The backend
 *     refuses a second active pack for the same `(user, job)` and
 *     returns `existing_pack_id` in the detail body. The section
 *     surfaces an inline calm "Open existing pack" affordance.
 *     No re-charge, no toast spam.
 *
 *   • RC 1.49.x.RUN_ISOLATION — Pack binds STRICTLY to a single
 *     `analyse_fit_id` derived from the URL workflow + matching
 *     `/best-fit` row. NO walking, NO "newest matching", NO
 *     fallback_last_analysis. When no match exists, `analyse_fit_id`
 *     is null and the pack reports `fit_status='missing'`.
 *
 *   • RC 1.49.x.TRANSIENT_SCORING Stage 1+2 — `pack_snapshot` is
 *     assembled from the matched `/best-fit` row when present.
 *     Source-CV-only packs may ALSO bind the canonical baseline
 *     via the existing `/baseline-fit` endpoint (Phase 4 inherits
 *     the wizard's "Fix B" path for symmetry).
 *
 *   • RC 1.49.5A P0-A3 — chargeable button hard-disabled while
 *     `creditsLoading`.
 *
 *   • RC 1.49.7 Phase 2 workspace UI rule — no floating panels.
 *     ONE primary action button per section.
 *
 *   • Pricing label sourced exclusively from
 *     `getActionCost(usageCosts, 'application_pack')`. No hidden
 *     charges (operator #8) — the cost shown to the user is
 *     exactly what the backend's `consume_usage` will spend.
 *
 *   • Three-control footer: Back · Create Application Pack ·
 *     Finish / View Pack. Continue is renamed "Finish" pre-pack
 *     and "View Pack" post-creation; both navigate to the
 *     /applications page (parity with wizard L3099).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Sparkles, Loader2, Layers,
  CheckCircle2, Info,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import {
  useCredits, getActionCost,
} from '@/lib/creditsContext';
import { handleCreditsError } from '@/lib/handleCreditsError';
// RC 1.49.7 Slice 6b (operator P0, 2026-02) — cache invalidation
// after pack creation. Pre-Slice-6b the workspace's pack POST
// succeeded and persisted, but the Applications page rendered from
// a 180-second-TTL `listCache` (`pages/ApplicationsPage.js:259`)
// that the workspace never invalidated — so a freshly-created pack
// was invisible for up to 3 minutes after the create. Wizard parity
// for this is `JobApplicationWizard.jsx:3094`.
import { invalidate as cacheInvalidate } from '@/lib/listCache';
// RC 1.52 Phase 3B.3 Pack Integration Hotfix-2 (operator-locked,
// 2026-05-11 PM late): `interviewPrepUnlock.js` is the legacy
// localStorage marker module from the piggy-back era. Under the
// standalone model, the durable `application_documents` row IS the
// unlock signal — checked via `interviewPrepDoc.status === 'complete'`.
// The import is removed to prevent a future regression where a UI
// gate accidentally reads the dead marker.
// RC 1.49.7 Slice 5b (operator P0, 2026-02) — `cv_variant` Analyse Fit
// is intentionally TRANSIENT per RC 1.49.x.TRANSIENT_SCORING Stage 2:
// the row is never persisted to `job_scores`, so `analyse_fit_id` is
// always null for variant-bound packs and `pack_snapshot.fit` is
// populated from the wizard-owned sessionStorage guard instead.
import {
  readVariantFitGuard,
  snapshotFitFromVariantPayload,
} from '@/lib/variantFitGuard';

// ─── RC 1.52 Phase 3A.DUP — system-name minting via shared helper ──
// Application Pack names are SYSTEM-generated and collision-proof
// (Application Pack — YYYY-MM-DD HH:MM:SS.mmm — XXXX). The user
// does NOT rename packs in the UI.
import { mintApplicationPackName } from '@/lib/titleStamps';

export default function PackSection({
  job,
  jobId,
  primaryCv,
  onContinue: _onContinue, // unused — Pack owns its own terminal nav
  onBack,
  onBusyChange,
  // RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
  workspaceBusy = false,
  rootTestId = 'workspace-pack-section',
}) {
  const navigate = useNavigate();
  const credits = useCredits();
  const usageCosts = credits?.usageCosts;
  const creditsLoading = !credits || !!credits.loading;

  const packCost = getActionCost(usageCosts, 'application_pack');
  // RC 1.49.7 Slice 3 — alias for the inline billing summary card
  // resolution. Preserves the same source of truth (admin-editable
  // `credit_action_prices` table via `usageCosts.application_pack`).
  const packCostFromCatalogue = packCost;
  const packCostLabel = packCost == null
    ? null
    : packCost === 0
      ? 'Included'
      : `${packCost} ${packCost === 1 ? 'Credit' : 'Credits'}`;

  // ── URL workflow state (READ-ONLY) ──────────────────────────────
  const [searchParams] = useSearchParams();
  const sourceCvType = searchParams.get('source_cv_type') || 'base_cv';
  const sourceCvId = searchParams.get('source_cv_id') || primaryCv?.id || null;
  const cvVariantId = sourceCvType === 'cv_variant' ? sourceCvId : null;
  const coverLetterId = searchParams.get('cover_letter_id') || null;
  const backendSourceType = sourceCvType === 'cv_variant' ? 'cv_variant' : 'uploaded_cv';

  // ── RC 1.49.7 Slice 5 — Analyse-fit URL triple (operator P0,
  //    2026-02). AnalyseFitSection writes `analyse_fit_id`,
  //    `analyse_fit_source_type`, `analyse_fit_source_id` to the
  //    URL the moment it binds a result (live run, in-flight
  //    recovery, or `/best-fit` mount hydration). We prefer that
  //    deterministic state over a fresh `/best-fit` lookup so a
  //    just-completed fit can never silently drop from the pack
  //    snapshot due to a read-after-write cache race.
  //
  //    Stale triples (pointing at a different source than the
  //    current URL CV source) are STRICTLY ignored — we never
  //    bind cross-source fit. AnalyseFitSection clears stale
  //    triples on the source-mismatch branch of its mirror
  //    effect; this read here is defensive.
  const urlAnalyseFitId         = searchParams.get('analyse_fit_id') || null;
  const urlAnalyseFitSourceType = searchParams.get('analyse_fit_source_type') || null;
  const urlAnalyseFitSourceId   = searchParams.get('analyse_fit_source_id') || null;
  const urlTripleMatchesSource = !!(
    urlAnalyseFitId
    && urlAnalyseFitSourceType === backendSourceType
    && urlAnalyseFitSourceId === sourceCvId
  );

  // ── RC 1.49.7 Slice 5b — Variant transient-fit carry-forward ─────
  //
  // `cv_variant` Analyse Fit is intentionally TRANSIENT per
  // RC 1.49.x.TRANSIENT_SCORING Stage 2 (operator-locked, 2026-05-06):
  // the row is never persisted to `job_scores`, so:
  //   • there is no `score.id` on the wire,
  //   • no row appears in `/best-fit`,
  //   • `analyse_fit_id` MUST stay null on the pack POST.
  //
  // The variant fit data is carried via `pack_snapshot.fit` instead.
  // AnalyseFitSection mirrors the live `/analyze-fit` response into
  // the wizard-owned sessionStorage guard (`variantFitGuard:*`) on
  // every successful run; this section reads that guard at render
  // time to drive the summary, interview-pack availability, and the
  // snapshot-fit assembly inside `handleCreate`.
  //
  // Strict source-match: the read is per-(user, job, variant) by
  // key, AND the helper additionally validates the stored payload's
  // `billing.source_type/source_id` matches the current URL source.
  // No cross-job, no cross-variant, no cross-user. TTL is 30 min
  // (wizard parity); stale entries return null.
  const variantGuardPayload = useMemo(() => {
    if (backendSourceType !== 'cv_variant') return null;
    if (!primaryCv?.user_id || !jobId || !sourceCvId) return null;
    return readVariantFitGuard({
      userId:    primaryCv.user_id,
      jobId,
      variantId: sourceCvId,
    });
  }, [backendSourceType, primaryCv?.user_id, jobId, sourceCvId]);

  // ── Pre-flight hydration (READ-ONLY, zero charge) ───────────────
  // Two parallel reads:
  //   1. /best-fit — for the source-matching cached fit row that
  //      drives `analyse_fit_id` + `pack_snapshot.fit`.
  //   2. /baseline-fit — for Source-CV-only packs that haven't run
  //      Step 3 (Fix B path).
  const [fitRow, setFitRow] = useState(null);              // matched /best-fit row | null
  const [baselineFit, setBaselineFit] = useState(null);    // /baseline-fit response | null
  // RC 1.49.7 C6/G3 (operator 2026-02 follow-up) — `hydrationRefSourceRef`
  // dropped after Stage 2 Batch A confirmed it suffered the SAME
  // StrictMode race as the boolean flip-before-fetch refs: run #1
  // sets ref → fires fetch → returns cleanup; run #2 sees ref already
  // equal currentKey and early-returns; run #1's resolution sees
  // cancelled=true → `setHydrating(false)` in the finally block was
  // GUARDED by `if (!cancelled)` and so SKIPPED, leaving hydrating
  // stuck at true forever and the Create button permanently
  // disabled. The dep array `[jobId, primaryCv?.user_id, sourceCvId,
  // backendSourceType]` already provides the per-source idempotency
  // we need; the cancelled flag + `if (!cancelled)` guards keep
  // stale-source state out of the UI.
  const [hydrating, setHydrating] = useState(true);

  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !sourceCvId) return undefined;

    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const [bfRes, baseRes] = await Promise.allSettled([
          apiFetch(`/api/jobs/${jobId}/best-fit`),
          apiFetch(`/api/jobs/${jobId}/baseline-fit`),
        ]);
        if (cancelled) return;
        if (bfRes.status === 'fulfilled') {
          const rows = Array.isArray(bfRes.value?.all_cached) ? bfRes.value.all_cached : [];
          const match = rows.find(
            (r) => r?.source_type === backendSourceType
              && r?.source_id === sourceCvId,
          );
          setFitRow(match || null);
        }
        if (baseRes.status === 'fulfilled') {
          setBaselineFit(baseRes.value || null);
        }
      } catch {
        // Fail-open — Pack create still works, just without
        // pack_snapshot.fit / analyse_fit_id.
      } finally {
        // RC 1.49.7 C6/G3 — `setHydrating(false)` MUST run even when
        // cancelled (a cancelled run-1 in StrictMode would otherwise
        // leave hydrating=true permanently because run-2 early-returns
        // via the dep-stability check). React's setState on an
        // unmounted component is a benign no-op (and the cleanup has
        // already torn down any subscriptions); the fail-open default
        // is preferable to a stuck-disabled button.
        setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, primaryCv?.user_id, sourceCvId, backendSourceType]);

  // ── Existing-pack probe (duplicate-pack inline UI) ──────────────
  // Mirrors the wizard's pre-creation lookup at L2567. The 409
  // backend guard is the source of truth; this probe just lets us
  // surface the existing pack inline BEFORE the user clicks Create
  // (calmer than a 409 toast after click).
  //
  // RC 1.49.7 C6/G3 (operator 2026-02) — `existingProbedRef` dropped
  // to be StrictMode-safe (boolean flip-before-fetch races with
  // cleanup-cancellation in dev). `cancelled` flag + dep-array
  // equality keep this idempotent.
  // RC 1.58 Phase 3D (operator-locked, 2026-02-14) — distinguish
  // "existingPack probe still in flight" from "probe complete with
  // no canonical pack". Set to `true` in the probe's `.then` /
  // `.catch` so the reconciliation effect below can trust the
  // `existingPack` value as a definite server-side answer.
  const [existingProbeComplete, setExistingProbeComplete] = useState(false);
  const [existingPack, setExistingPack] = useState(null);

  useEffect(() => {
    if (!jobId) return undefined;
    // RC 1.51 follow-up E4 (operator-locked, 2026-02) — clear the
    // previous job's existingPack BEFORE the new fetch resolves.
    // Without this, navigating workspace → workspace within the
    // same SPA session leaves stale state in memory until the new
    // probe lands (~200-400 ms), which would flash the wrong job's
    // pack on the inline existing-pack notice. The primary CTA is
    // already protected by E1 (it never reads existingPack), but
    // the informational notice still benefits from this defence.
    setExistingPack(null);
    // RC 1.58 Phase 3D — reset the probe-complete flag on jobId
    // change so the reconciliation effect waits for the new probe.
    setExistingProbeComplete(false);
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(
          `/api/application-packs?job_id=${encodeURIComponent(jobId)}&page_size=1`,
        );
        if (cancelled) return;
        const rows = Array.isArray(r?.items) ? r.items : Array.isArray(r) ? r : [];
        const active = rows.find((p) => p?.pack_status && p.pack_status !== 'archived');
        setExistingPack(active || null);
      } catch {
        // Fail-open.
      } finally {
        if (!cancelled) setExistingProbeComplete(true);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  // ── Created pack state ──────────────────────────────────────────
  //
  // RC 1.58 Phase 3A (2026-02-14, operator-locked) — `createdPack`
  // is now persisted in `sessionStorage` keyed by jobId so the
  // "Open Application Pack" CTA continuity survives:
  //   • workspace step navigation (Back / Forward)
  //   • tab switches that unmount the section
  //   • route transitions that remount the PackSection
  // Tab close still clears (sessionStorage is per-tab) — that is
  // the intended boundary (a new tab is a new workspace session).
  //
  // The persisted shape is the minimum needed to drive the footer
  // CTA navigation: `{ id, created_at, billing? }`. No scoring,
  // billing, or orchestration mutation.
  const _packStorageKey = jobId ? `workspace:createdPack:${jobId}` : null;
  const [createdPack, setCreatedPack] = useState(() => {
    if (typeof window === 'undefined' || !_packStorageKey) return null;
    try {
      const raw = window.sessionStorage.getItem(_packStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object' && parsed.id) ? parsed : null;
    } catch {
      return null;
    }
  });
  // Rehydrate when jobId changes (workspace → workspace navigation).
  useEffect(() => {
    if (typeof window === 'undefined' || !_packStorageKey) {
      setCreatedPack(null);
      return;
    }
    try {
      const raw = window.sessionStorage.getItem(_packStorageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      setCreatedPack(
        parsed && typeof parsed === 'object' && parsed.id ? parsed : null,
      );
    } catch {
      setCreatedPack(null);
    }
  }, [_packStorageKey]);
  // Persist on every successful `setCreatedPack(pack)` write.
  useEffect(() => {
    if (typeof window === 'undefined' || !_packStorageKey) return;
    try {
      if (createdPack && createdPack.id) {
        const minimal = {
          id: createdPack.id,
          created_at: createdPack.created_at,
          billing: createdPack.billing,
        };
        window.sessionStorage.setItem(_packStorageKey, JSON.stringify(minimal));
      }
      // Note: we intentionally do NOT remove the key on `createdPack
      // === null` — that would erase the marker on transient renders.
      // The key has a natural lifetime: cleared by tab close, or
      // explicit operator reset (out of scope for this slice).
    } catch {
      /* fail-open */
    }
  }, [createdPack, _packStorageKey]);

  // RC 1.58 Phase 3D (operator-locked, 2026-02-14) — Reconciliation
  // effect. After the existingPack probe completes, validate the
  // hydrated createdPack against the canonical server view of this
  // job. If the stored id does not match the canonical pack id
  // (deleted / archived / regenerated since), clear both the in-
  // memory state AND the sessionStorage marker so the footer falls
  // back to "Create Application Pack" rather than offering a
  // navigation to a stale pack.
  //
  // Why this is needed: Phase 3A's hydration trusted the stored id
  // verbatim. Operator reported entering the Pack step and seeing
  // "Open Application Pack" → clicking → landing on an empty
  // Applications page because the persisted id no longer pointed
  // at a real pack.
  //
  // Strict-hold: presentation / local state only. No backend call
  // added (the probe was already firing on mount). No new endpoint.
  useEffect(() => {
    if (!_packStorageKey) return;
    if (!existingProbeComplete) return;        // probe still in flight
    if (!createdPack || !createdPack.id) return; // nothing to reconcile
    if (existingPack?.id === createdPack.id) return; // canonical match
    // Stored id is stale → clear both in-memory state AND the
    // sessionStorage marker so the next mount also starts clean.
    setCreatedPack(null);
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(_packStorageKey);
      }
    } catch {
      /* fail-open */
    }
  }, [
    existingPack,
    existingProbeComplete,
    createdPack,
    _packStorageKey,
  ]);
  const [creating, setCreating] = useState(false);
  // RC 1.50 Workspace UX Slice 0 — emit busy state to parent.
  useEffect(() => {
    onBusyChange?.(creating);
  }, [creating, onBusyChange]);

  // ── RC 1.51 follow-up F4 (operator-locked, 2026-02) ────────────
  //
  // Workspace Interview Prep is a SEPARATELY PAID action
  // (`interview_prep` @ 15 credits, distinct from baseline
  // `analyze_fit` @ 9 credits). Entitlement to include Interview
  // Pack content in an Application Pack is therefore:
  //
  //     interview_prep action success → and ONLY that.
  //
  // Baseline Analyse Fit data MUST NOT grant Interview Prep for
  // free, even when the underlying analyse_run row carries
  // `reasoning_json.interview_prep`. The `interviewPrepUnlock`
  // localStorage marker is written exclusively by the
  // `interview_prep` adapter's success path
  // (`InterviewPrepSection.handleRun`), so it is the canonical
  // (Hotfix-2 derivation moved BELOW the state declaration — see
  // L367, after `interviewPrepDoc` is initialised. The forward-
  // reference here would TDZ-error on first render.)

  // ── RC 1.52 Phase 3B.3 Pack Integration ─────────────────────────
  //
  // Workspace Interview Prep is a standalone first-class action
  // persisted in `application_documents(document_type='interview_prep')`.
  // The pack snapshot reads from that durable row, NOT from any
  // localStorage cache.
  //
  // Strict source-match: latest completed row whose
  // `content_json.job_id` AND (`base_cv_id` OR `cv_variant_id`)
  // match the current URL workflow. We additionally sort by
  // `created_at desc` and `limit=1`, so a stale cancelled run
  // never wins over a fresh complete one.
  //
  // States exposed:
  //   • `interviewPrepDoc.row`        — the matched row (or null).
  //   • `interviewPrepDoc.status`     — 'complete' | 'queued' |
  //                                     'running' | 'failed' |
  //                                     'absent'.
  //   • `interviewPrepDoc.errorCode`  — surfaced on failed rows.
  //   • `interviewPrepDoc.payload`    — the populated
  //                                     `content_json.interview_prep`
  //                                     object on complete rows.
  //                                     `null` on every non-complete
  //                                     state — see L489 snapshot
  //                                     assembly.
  const [interviewPrepDoc, setInterviewPrepDoc] = useState({
    row:       null,
    status:    'absent',
    errorCode: null,
    payload:   null,
  });

  // RC 1.52 Phase 3B.3 Pack Integration Hotfix-2 (operator-locked,
  // 2026-05-11 PM late) — the workspace "Interview Prep unlocked
  // for this pack" signal is the standalone
  // `application_documents(document_type='interview_prep')` row,
  // NOT the dead localStorage marker. `interviewPrepDoc` is hydrated
  // by the effect below; `status === 'complete'` is the
  // authoritative signal.
  //
  // The legacy `isInterviewPrepUnlocked(localStorage)` reader has
  // been REMOVED. Under the standalone model the section no longer
  // writes that marker, so it returned false even when a fresh
  // populated row existed — driving the visible regression
  // "Interview Pack unavailable" / "No generated artefacts yet".
  const interviewPrepUnlockedForPack = interviewPrepDoc.status === 'complete';
  const interviewPackAvailable       = interviewPrepUnlockedForPack;

  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !sourceCvId) return undefined;

    let cancelled = false;

    // RC 1.52 Phase 3B.3 Pack Integration Hotfix (operator-locked,
    // 2026-05-11 PM late): the list endpoint is a TYPED FastAPI
    // route, not a PostgREST passthrough. Send plain query params
    // — the route handler injects `user_id=eq.{me}` server-side
    // from the JWT, and converts each plain param into the
    // PostgREST filter shape when proxying to Supabase. Sending
    // PostgREST raw filters (`document_type=eq.…`, `content_json
    // ->>job_id=eq.…`) collides with the typed Query() declarations
    // and 400s every request.
    const params = new URLSearchParams({
      document_type: 'interview_prep',
      job_id:        jobId,
      limit:         '5',
    });
    if (backendSourceType === 'cv_variant') {
      params.set('cv_variant_id', sourceCvId);
    } else {
      params.set('base_cv_id', sourceCvId);
    }

    // Mid-flow URL recovery: when the workspace was navigated with
    // `?interview_prep_id={id}` in the URL (refresh / tab-return),
    // we can short-circuit to the singular GET endpoint without
    // hitting the list lane at all. We keep the list query as the
    // primary so the latest completed row always wins.
    const urlIpId = searchParams.get('interview_prep_id');

    (async () => {
      try {
        // Primary path: list the latest matching rows via the
        // typed list endpoint.
        let rows = null;
        try {
          rows = await apiFetch(`/api/application-documents?${params.toString()}`);
        } catch {
          rows = null;
        }
        if (cancelled) return;
        let chosen = null;
        if (Array.isArray(rows) && rows.length) {
          chosen = rows.find((r) => r?.run_status === 'complete')
                || rows.find((r) => r?.run_status === 'running')
                || rows.find((r) => r?.run_status === 'queued')
                || rows.find((r) => r?.run_status === 'failed')
                || rows[0] || null;
        } else if (urlIpId) {
          // Fallback path — read the single document via the
          // standalone GET. Header-gated to the canonical envelope
          // so we get a stable shape.
          try {
            const env = await apiFetch(`/api/interview-prep/${urlIpId}`, {
              headers: { 'X-Envelope': 'rc_1_52_canonical' },
            });
            if (env && (env.status || env.run_status)) {
              chosen = {
                id:               env.run_id || urlIpId,
                run_status:       (env.status || '').toLowerCase()
                                  || env.run_status,
                run_error_code:   env.error_code || null,
                run_error_detail: env.error_detail || null,
                content_json: {
                  job_id:         jobId,
                  interview_prep: env.result?.interview_prep
                                  || env?.interview_prep
                                  || null,
                },
                created_at:       env.created_at || null,
              };
            }
          } catch {
            /* noop — final state is 'absent' */
          }
        }
        if (cancelled) return;
        if (!chosen) {
          setInterviewPrepDoc({
            row: null, status: 'absent', errorCode: null, payload: null,
          });
          return;
        }
        const status = (chosen.run_status || '').toLowerCase() || 'absent';
        const payload = status === 'complete'
          ? (chosen.content_json?.interview_prep || null)
          : null;
        setInterviewPrepDoc({
          row:       chosen,
          status,
          errorCode: chosen.run_error_code || null,
          payload,
        });
      } catch {
        if (!cancelled) {
          setInterviewPrepDoc({
            row: null, status: 'absent', errorCode: null, payload: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    jobId, primaryCv?.user_id, sourceCvId, backendSourceType,
    searchParams,
  ]);

  // ── Pre-flight credits gate (toast-only Phase 4) ────────────────
  const ensureCreditsAvailable = (actionType, actionLabel) => {
    if (creditsLoading) return false;
    const cost = getActionCost(usageCosts, actionType);
    if (cost == null || cost === 0) return true;
    const available =
      Number(credits?.includedUsageRemaining || 0)
      + Number(credits?.extraUsageRemaining || 0);
    if (available >= cost) return true;
    toast.error(`Not enough Credits for ${actionLabel}`, {
      description: `${available} available · ${cost} needed. Open Plan to add Credits.`,
      action: {
        label: 'View plan',
        onClick: () => {
          try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
        },
      },
    });
    return false;
  };

  // ── Create handler ──────────────────────────────────────────────
  const handleCreate = async () => {
    if (!primaryCv?.id) {
      toast.error('No Source CV. Upload one in Documents → CVs.');
      return;
    }
    if (!ensureCreditsAvailable('application_pack', 'Create application pack')) return;

    // Resolve the analyse_fit_id binding STRICTLY per RUN_ISOLATION
    // and per RC 1.49.7 Slice 5 + Slice 5b (operator P0, 2026-02).
    // Priority:
    //   1. URL triple matching the current source. AnalyseFitSection
    //      writes this the moment it binds a result, so a freshly-
    //      completed fit is never lost to a `/best-fit` cache lag.
    //      Only fires for `uploaded_cv` in practice — `cv_variant`
    //      runs never produce a `score.id` (Stage 2 transient).
    //   2. `/best-fit` row matching the current source.
    //   3. `cv_variant` source + fresh sessionStorage guard
    //      (RC 1.49.x.TRANSIENT_SCORING Stage 2): `analyse_fit_id`
    //      MUST stay null (there is no FK row to bind), but
    //      `pack_snapshot.fit` is populated from the stored payload.
    //   4. Source-CV baseline (Fix B) — ONLY for uploaded_cv when
    //      neither (1) nor (2) is available.
    //   5. None — pack saves with `fit_status='missing'`.
    //
    // We NEVER fall back to "latest historical", "any source", or a
    // best-fit row whose source_type/source_id does not match the
    // current URL workflow. Stale URL triples are excluded by the
    // `urlTripleMatchesSource` gate; stale variant guards are
    // excluded by the helper's TTL + source-equality check.
    let analyseFitIdToBind = null;
    if (urlTripleMatchesSource) {
      analyseFitIdToBind = urlAnalyseFitId;
    } else if (fitRow?.score_id) {
      // Both source_type + source_id already match (we filtered for
      // this in the hydration effect).
      analyseFitIdToBind = fitRow.score_id;
    } else if (
      backendSourceType === 'uploaded_cv'
      && sourceCvId
      && baselineFit?.baseline?.score_id
      && baselineFit?.primary_cv?.id === primaryCv.id
    ) {
      // Fix B — Source-CV-only baseline binding.
      analyseFitIdToBind = baselineFit.baseline.score_id;
    }
    // NOTE: the `cv_variant` + `variantGuardPayload` branch
    // intentionally does NOT bind `analyse_fit_id` — see the snapshot
    // assembly below for the carry-forward path.

    // Assemble the canonical snapshot (parity with wizard L2998).
    //
    // RC 1.49.7 Slice 5b — `cv_variant` transient runs build the
    // snapshot from the wizard-owned sessionStorage guard, with the
    // SAME field shape the wizard mints from `step3LastResult.scoreRow`
    // (`JobApplicationWizard.jsx:2231-2242`). This keeps a wizard-
    // created and a workspace-created pack carrying byte-identical
    // `pack_snapshot.fit` blobs for the same underlying run.
    let snapshotFit = null;
    if (fitRow) {
      snapshotFit = {
        score_id:        fitRow.score_id,
        fit_score:       fitRow.fit_score,
        source_type:     fitRow.source_type,
        source_id:       fitRow.source_id,
        reasoning_json:  fitRow.reasoning_json ?? null,
        salary_score:    fitRow.salary_score ?? null,
        seniority_score: fitRow.seniority_score ?? null,
        location_score:  fitRow.location_score ?? null,
        strategic_score: fitRow.strategic_score ?? null,
        risk_score:      fitRow.risk_score ?? null,
        final_decision:  fitRow.final_decision ?? null,
        scored_with_letter: fitRow.scored_with_letter ?? null,
      };
    } else if (
      backendSourceType === 'cv_variant'
      && variantGuardPayload
    ) {
      // Re-read at create time so a stale-by-now guard (possible if
      // the user lingered ≥30 min between Fit and Pack) is rejected
      // here too — the memo is render-bound and could otherwise
      // surface a freshly-expired payload.
      const liveGuard = readVariantFitGuard({
        userId:    primaryCv.user_id,
        jobId,
        variantId: sourceCvId,
      });
      if (liveGuard) snapshotFit = snapshotFitFromVariantPayload(liveGuard);
    }

    const sourceBaseline = baselineFit?.baseline?.score_id
      && baselineFit?.primary_cv?.id === primaryCv.id
      ? baselineFit.baseline
      : null;

    // RC 1.52 Phase 3B.3 Pack Integration (operator-locked, 2026-05-11 PM):
    // Read the Workspace Interview Prep payload from the standalone
    // `application_documents(document_type='interview_prep')` row
    // hydrated by the effect at L334. The legacy localStorage cache
    // (`getInterviewPrepResult`) is REMOVED — InterviewPrepSection no
    // longer writes it under the standalone model. The durable row
    // is the canonical source of truth.
    //
    // Behaviour matrix:
    //   • complete  → payload populated → Cover Sheet + Interview
    //                 Intelligence PDF included in pack export.
    //   • queued / running → payload=null → pack creates without
    //                 Interview Prep. Banner above the Create button
    //                 warns "Interview Prep still generating; pack
    //                 will skip it" (see render block).
    //   • failed    → payload=null → pack creates without Interview
    //                 Prep. Banner shows the friendly failure reason.
    //   • absent    → payload=null → pack creates cleanly.
    //
    // The backend export gate (`_interview_prep_from_pack_snapshot`)
    // already reads `pack_snapshot.interview_prep` verbatim and
    // returns None when null. Legacy nested-path fallback for
    // pre-RC-1.51 packs is preserved at routes/application_packs.py.
    const interviewPrepPayloadForSnapshot = interviewPrepDoc.payload || null;

    const packSnapshot = {
      binding: {
        cv_variant_id:    cvVariantId,
        cover_letter_id:  coverLetterId,
        analyse_fit_id:   analyseFitIdToBind,
      },
      fit:             snapshotFit,
      // RC 1.49.7 Slice 5b — `cv_verdict` parity with wizard. For
      // variant transient runs, the strict CV sub-score lives inside
      // the embedded `snapshotFit.reasoning_json`; for /best-fit
      // runs it lives in `fitRow.reasoning_json`. Surface whichever
      // is present so the pack page renders consistently across paths.
      cv_verdict:      fitRow?.reasoning_json?.cv_sub_score
                      ?? snapshotFit?.reasoning_json?.cv_sub_score
                      ?? null,
      source_baseline: sourceBaseline,
      // RC 1.51 Pack Snapshot Separation (operator-locked, 2026-02)
      // — Workspace Interview Prep is now a TOP-LEVEL snapshot field,
      // independent of `fit.reasoning_json`. Source-CV-only packs
      // can carry Interview Prep without any Variant Fit row. The
      // backend export gate widens to allow Interview Intelligence
      // PDF + Cover Sheet whenever this field is populated; the
      // legacy nested path remains supported for old packs.
      interview_prep:  interviewPrepPayloadForSnapshot,
    };

    setCreating(true);
    try {
      const body = {
        job_id:          jobId,
        base_cv_id:      primaryCv.id,
        cv_variant_id:   cvVariantId,
        cover_letter_id: coverLetterId,
        analyse_fit_id:  analyseFitIdToBind,
        pack_status:     'draft',
        pack_name:       mintApplicationPackName(),
        notes:           null,
        pack_snapshot:   packSnapshot,
      };
      const pack = await apiFetch('/api/application-packs', {
        method: 'POST',
        body,
      });
      setCreatedPack(pack);
      // RC 1.58 Phase 3D — defensively seed `existingPack` with the
      // just-created row so the reconciliation effect (above) does
      // not race-clear the freshly-written createdPack before the
      // probe re-fetches the canonical view. The probe still runs
      // on the next jobId-change cycle; this is a same-render
      // alignment to keep both surfaces consistent.
      setExistingPack(pack);
      setExistingProbeComplete(true);
      // RC 1.49.7 Slice 6b — drop list caches the wizard's pack-
      // create path also invalidates (`JobApplicationWizard.jsx
      // :3094`). Without this, ApplicationsPage / DashboardPage
      // serve stale data from `listCache` (180-second TTL) until
      // the user hard-refreshes. Same key vector as the wizard so
      // both surfaces converge on a single source of truth.
      cacheInvalidate([
        'applications',
        'application-packs:all',
        'jobs',
        'dashboard:bundle',
      ]);
      toast.success('Application pack created.');
      if (typeof credits?.refresh === 'function') {
        try { credits.refresh(); } catch { /* noop */ }
      }
    } catch (err) {
      // Operator #6 — duplicate-pack 409 guard surfaces inline.
      const status = err?.status || err?.response?.status;
      const det = err?.data?.detail;
      const isDup = status === 409 && det && typeof det === 'object'
                    && det.error === 'duplicate_active_pack'
                    && det.existing_pack_id;
      if (isDup) {
        setExistingPack({
          id:           det.existing_pack_id,
          pack_status:  det.existing_pack_status,
          pack_name:    det.existing_pack_name,
          created_at:   det.existing_pack_created_at,
        });
        toast.info(
          det.message || 'You already have an active pack for this job.',
          {
            action: {
              label: 'View in Applications',
              onClick: () => navigate(
                `/applications?highlight=${encodeURIComponent(det.existing_pack_id)}`,
              ),
            },
          },
        );
        return;
      }
      if (handleCreditsError(err, {
        action: 'Create application pack',
        refreshCredits: credits?.refresh,
      })) return;
      toast.error(err?.data?.detail?.message || err?.message || 'Failed to create pack.');
    } finally {
      setCreating(false);
    }
  };

  // ── Terminal nav ────────────────────────────────────────────────
  // RC 1.51 follow-up F3 (operator-locked, 2026-02) — the legacy
  // `handleFinish` + `finishLabel` + dedicated Continue/Finish
  // button have been REMOVED. The single state-driven primary
  // footer CTA below absorbs both roles ("Create" vs "Open"
  // depending on whether a pack already exists for this
  // (user, job)). Direct-open of /applications/<packId> is no
  // longer used anywhere in PackSection — Applications list
  // (`/applications?highlight=<id>`) is the landing experience.

  // ── Render helpers ──────────────────────────────────────────────
  // RC 1.50 polish-clarification — the Application Pack is built
  // from GENERATED ARTEFACTS only. The Source CV is an INPUT, not
  // a deliverable: it is never exported as part of the pack and
  // is never listed in the deliverables checklist. The pack
  // contract requires AT LEAST ONE of:
  //   • Tailored CV (sourceType === 'cv_variant'),
  //   • Cover Letter (coverLetterId bound),
  //   • Interview Prep (Workspace unlock marker present).
  // The Create button is disabled until at least one is true.
  // RC 1.51 follow-up F4 — `interviewPrepUnlockedForPack` is
  // declared once at L324 (single source of truth for Interview
  // Prep entitlement) and referenced from here.
  const tailoredCvAvailable = sourceCvType === 'cv_variant';
  const hasAnyGeneratedArtefact = !!(
    tailoredCvAvailable
    || coverLetterId
    || interviewPrepUnlockedForPack
  );

  // RC 1.51 follow-up F2 (operator-locked, 2026-02) — existing
  // packs are INFORMATIONAL only. They no longer hard-block the
  // create flow. The backend's 409 duplicate-active-pack guard
  // (`application_packs.py` L368-403) remains as defence-in-depth;
  // its catch path (L520-545) surfaces the existing pack inline
  // and lands the user on `/applications?highlight=<id>`.
  const createDisabled = creating || hydrating || creditsLoading
    || !primaryCv?.id || !!createdPack
    || !hasAnyGeneratedArtefact
    /* RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock. */
    || workspaceBusy;

  return (
    <div
      data-testid={rootTestId}
      className="flex flex-col gap-5 min-w-0 max-w-full"
    >
      {/* ── Header with live pricing ─────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-ndor-ink">
            Application Pack
          </h2>
          {packCostLabel != null && (
            <Badge
              className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0"
              data-testid="workspace-pack-price-label"
            >
              {packCostLabel}
            </Badge>
          )}
        </div>
        <p
          className="text-xs text-ndor-ink-muted"
          data-testid="workspace-pack-tagline"
        >
          Bundles your tailored CV {coverLetterId ? '+ cover letter ' : ''}+ interview prep into one Application Pack you can share.
          {' '}
          <span className="text-ndor-ink-muted/80">
            All three documents stay saved in Documents, so a Pack can be reassembled later from there if needed.
          </span>
        </p>
      </div>

      {/* ── RC 1.50 R.4 — Hydrating banner (parity with CV / Letter
            / Fit sections). Renders only while the existing-pack
            lookup is in flight so the user sees a calm "we're
            checking" state instead of a flash of "no pack" before
            existingPack lands. ──────────────────────────────────── */}
      {hydrating && (
        <div
          data-testid="workspace-pack-restoring-banner"
          className="rounded-md border border-border/50 bg-background/60 p-2.5 flex items-center gap-2 text-[11px] text-ndor-ink-muted"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
          <span>Restoring your application pack state…</span>
        </div>
      )}

      {/* ── Workflow summary card (read-only) ───────────────────── */}
      {/* RC 1.50 polish-clarification — Source CV is an INPUT, not
            a pack deliverable. The list below renders only
            GENERATED artefacts. When zero generated artefacts
            exist we render the calm "no artefacts yet" notice
            instead of a checklist with skipped placeholders. ─── */}
      {/* ── Pack checklist (RC 1.51 follow-up — included / skipped /
            input-only semantics) ─────────────────────────────── */}
      <div
        data-testid="workspace-pack-summary-card"
        className="rounded-lg border border-ndor-line p-3 space-y-2"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Layers className="h-4 w-4 text-ndor-ink-muted shrink-0" />
          <p className="text-sm font-medium text-ndor-ink">
            Pack will bundle:
          </p>
        </div>
        {!hasAnyGeneratedArtefact ? (
          <p
            data-testid="workspace-pack-summary-empty"
            className="text-[12px] text-ndor-ink-muted leading-relaxed"
          >
            No generated artefacts yet — generate at least one artefact
            (Tailored CV, Cover Letter, or Interview Prep) to create a pack.
          </p>
        ) : (
          <ul className="text-[12px] text-ndor-ink-muted leading-relaxed space-y-1 pl-5 list-disc">
            {/* RC 1.51 follow-up — checklist communicates
                  pack-membership semantics (included / skipped /
                  input-only), NOT system object existence.
                  Source CV is the input the pack was tailored
                  from; it is reference-only and never exported. */}
            {tailoredCvAvailable ? (
              <li data-testid="workspace-pack-summary-tailored-cv">
                Tailored CV — included
              </li>
            ) : (
              <li data-testid="workspace-pack-summary-source-cv">
                Source CV used — not exported
              </li>
            )}
            <li data-testid="workspace-pack-summary-letter">
              Cover Letter — {coverLetterId ? 'included' : 'skipped'}
            </li>
            <li data-testid="workspace-pack-summary-fit">
              Interview Prep — {interviewPrepUnlockedForPack ? 'included' : 'skipped'}
            </li>
          </ul>
        )}
      </div>

      {/* ── Interview Pack availability notice ──────────────────── */}
      {!interviewPackAvailable && !hydrating && (
        <div
          data-testid="workspace-pack-interview-locked"
          className="rounded-lg border border-ndor-line bg-ndor-bg-subtle p-3 space-y-2"
        >
          <p
            className="text-sm text-ndor-ink flex items-center gap-1.5"
            data-testid="workspace-pack-interview-locked-copy"
          >
            <Sparkles className="h-3.5 w-3.5 text-ndor-gold shrink-0" />
            Interview Pack unavailable until Interview Prep is generated for this application workflow.
          </p>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed">
            Optional — your pack will still be created. Generate Interview Prep
            on the previous step to unlock gap-aware interview questions,
            repositioning advice, and risk briefings tailored to this role.
          </p>
        </div>
      )}

      {/* ── Historical previous-pack strip (informational only) ────
            RC 1.58 Phase 3A (operator-locked, 2026-02-14) — the
            previously generated pack is HISTORICAL CONTEXT only.
            It is NOT evidence that the current workspace session
            owns a generated pack. Therefore:
              • non-clickable (no Button, no onClick, no pointer)
              • no hover affordance / no deep-link to Applications
              • relabelled "Most recent previously generated pack"
              • visually muted vs the active/current state
              • SUPPRESSED entirely when the historical row id
                matches the current session's createdPack.id
                (avoids double-rendering the same pack as both
                "previous" and "current")
            The backend's 409 duplicate-active-pack guard remains
            as defence-in-depth. ───────────────────────────────── */}
      {existingPack
        && !createdPack
        && existingPack.id !== createdPack?.id && (
        <div
          data-testid="workspace-pack-existing-notice"
          className="rounded-lg border border-ndor-line bg-ndor-bg-subtle p-3 space-y-1"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Info className="h-4 w-4 text-ndor-ink-muted shrink-0" />
            <p
              className="text-xs font-medium text-ndor-ink-muted"
              data-testid="workspace-pack-existing-notice-label"
            >
              Most recent previously generated pack
            </p>
          </div>
          <p
            className="text-[11px] text-ndor-ink-muted leading-relaxed pl-6"
            data-testid="workspace-pack-existing-notice-name"
          >
            {existingPack.pack_name || 'An earlier pack exists for this job.'}
          </p>
          <p
            className="text-[11px] text-ndor-ink-muted/80 leading-relaxed pl-6"
            data-testid="workspace-pack-existing-notice-hint"
          >
            Historical context only — not the current session's pack.
            Open it from the Applications page if you need to review it.
          </p>
        </div>
      )}

      {/* ── Created-pack confirmation card ───────────────────────── */}
      {createdPack && (
        <div
          data-testid="workspace-pack-created-card"
          className="rounded-lg border-2 border-ndor-success/40 bg-ndor-success/[0.05] p-3 space-y-2"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <CheckCircle2 className="h-4 w-4 text-ndor-success shrink-0" />
            <p className="text-sm font-medium text-ndor-ink truncate">
              Pack created.
            </p>
          </div>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed">
            Saved to Applications. The pack is highlighted there — you can
            review, export, or share it from the Applications list.
          </p>

          {/* RC 1.51 follow-up F3 (operator-locked, 2026-02) —
                inline post-create "Open Pack" button REMOVED. The
                state-driven footer CTA below absorbs this role
                ("Open Application Pack" navigates to
                /applications?highlight=<id>). Avoids duplicate
                conflicting actions. */}

          {/* ── RC 1.49.7 Slice 3 — Inline billing summary
                (operator P0, 2026-02). Render-only. Pack price is
                currently 0 across all plans, but the disclosure
                pattern stays uniform across the four sections so
                pricing changes surface immediately. Per
                billing_contract.md §6.1.
                Resolution order:
                  1. Wire `createdPack.billing.usage_consumed`
                     (preferred — matches the contract for
                     CV/Letter/Fit).
                  2. Wire `createdPack.usage_consumed` top-level
                     mirror.
                  3. Configured price 0 from the catalogue
                     (`packCostFromCatalogue === 0`) — surfaces as
                     "Included · 0 Credits" because we can deterministically
                     state the configured cost is zero.
                  4. Otherwise: render "—" graceful fallback.
                Never derives. ─────────────────────────────────── */}
          {(() => {
            const wireUsage = createdPack?.billing?.usage_consumed
              ?? createdPack?.usage_consumed;
            if (wireUsage != null) {
              return (
                <p
                  className={
                    'text-[11px] '
                    + (wireUsage > 0
                      ? 'text-ndor-warning'
                      : 'text-ndor-ink-muted')
                  }
                  data-testid="workspace-pack-billing-line"
                >
                  Charged{' '}
                  <span
                    className="font-mono-ui tabular-nums"
                    data-testid="workspace-pack-billing-usage-consumed"
                  >
                    {wireUsage}
                  </span>
                  {' '}credit{wireUsage === 1 ? '' : 's'}
                  {createdPack?.billing?.plan_code && (
                    <span className="ml-1 text-ndor-ink-muted">
                      · plan{' '}
                      <span
                        className="font-mono-ui"
                        data-testid="workspace-pack-billing-plan-code"
                      >
                        {createdPack.billing.plan_code}
                      </span>
                    </span>
                  )}
                </p>
              );
            }
            if (packCostFromCatalogue === 0) {
              return (
                <p
                  className="text-[11px] text-ndor-ink-muted"
                  data-testid="workspace-pack-billing-included"
                >
                  Included · 0 Credits charged.
                </p>
              );
            }
            return (
              <p
                className="text-[11px] text-ndor-ink-muted"
                data-testid="workspace-pack-billing-unavailable"
              >
                Billing details — <span aria-hidden>—</span>{' '}
                <span className="sr-only">not available for this saved pack.</span>
              </p>
            );
          })()}
        </div>
      )}

      {/* ── RC 1.51 follow-up F3 — Two-control footer
            (operator-locked, 2026-02). Single state-driven
            primary CTA: "Create Application Pack" when no pack
            exists, "Open Application Pack" when one does
            (createdPack OR existingPack). Both create + open
            land the user on /applications?highlight=<id>. ─── */}
      <footer
        data-testid="workspace-section-footer"
        /* RC 1.55 P0 Mobile pass — stack on <xs, wrap from xs+. */
        className="sticky bottom-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex flex-col xs:flex-row xs:flex-wrap xs:items-center xs:justify-between gap-2 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      >
        {/* RC 1.58 Phase 3A (operator-locked, 2026-02-14) — Back
            button hidden once the current workspace session owns a
            generated pack. The only forward path then is "Open
            Application Pack" below. Avoids the false-continuity
            state where a user could navigate back into the
            Interview Prep step after the pack already exists. */}
        {!createdPack && (
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
            data-testid="workspace-section-back-button"
            className="w-full xs:w-auto px-2 sm:px-3"
          >
            <ArrowLeft className="mr-1 sm:mr-1.5 h-3.5 w-3.5" />
            <span className="hidden sm:inline">Back to Interview Prep</span>
            <span className="sm:hidden">Back</span>
          </Button>
        )}

        {(() => {
          // RC 1.51 follow-up E1 (operator-locked, 2026-02) —
          // primary CTA = CURRENT WORKFLOW ONLY. The footer's
          // open-target is keyed off `createdPack?.id` exclusively;
          // `existingPack?.id` is INTENTIONALLY excluded.
          //
          // Why: the previous F1 gate (`hasAnyGeneratedArtefact ?
          // existingPack?.id : null`) only protected the empty-
          // Source-CV edge case. In the normal flow the user
          // arrives at the Pack step with `hasAnyGeneratedArtefact
          // === true`, so existingPack would hijack the primary
          // CTA the instant the mount-time probe resolved
          // (~200-400 ms after first paint). The visible flicker
          // "Create Application Pack → Open Application Pack" is
          // that hijack, and clicking the flipped button routed
          // the user to a PRIOR pack — possibly from a different
          // job during workspace navigation, where stale
          // existingPack state lingered until the new probe
          // landed.
          //
          // The inline existing-pack notice (L727-752) keeps the
          // "View in Applications" affordance for prior packs as
          // an informational signal. The primary CTA stays on
          // "Create Application Pack" until handleCreate's success
          // path stamps `createdPack` for THIS PackSection
          // instance. Multi-pack-per-(user, job) is now allowed
          // by the backend (E2), so this no longer dead-ends the
          // user when a prior pack exists.
          const openTargetId = createdPack?.id || null;
          if (openTargetId) {
            return (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(
                  `/applications?highlight=${encodeURIComponent(openTargetId)}`,
                )}
                /* RC 1.53 Phase 4.2.1.1.d — navigation to a created
                   Pack is always free even mid-flight. */
                data-testid="workspace-pack-create-button"
                className="w-full xs:w-auto xs:flex-1 xs:min-w-0 sm:flex-none px-2 sm:px-3"
              >
                <span className="hidden xs:inline truncate">Open Application Pack</span>
                <span className="xs:hidden truncate">Open Pack</span>
                <ArrowRight className="ml-1 sm:ml-1.5 h-3.5 w-3.5 shrink-0" />
              </Button>
            );
          }
          return (
            <Button
              variant="default"
              size="sm"
              onClick={handleCreate}
              disabled={createDisabled}
              data-testid="workspace-pack-create-button"
              className="w-full xs:w-auto xs:flex-1 xs:min-w-0 sm:flex-none px-2 sm:px-3"
            >
              {creating
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin shrink-0" />
                : <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
              <span className="hidden xs:inline truncate">Create Application Pack</span>
              <span className="xs:hidden truncate">Create Pack</span>
            </Button>
          );
        })()}
      </footer>

      {/* ── RC 1.52 Phase 3B.3 — Interview Prep readiness banner ── */}
      {!createdPack && interviewPrepDoc.status === 'running' && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200"
          data-testid="workspace-pack-interview-prep-running-banner"
        >
          <span className="font-semibold">Interview Prep is still generating.</span>{' '}
          If you create the pack now, it will be assembled without
          the Interview Intelligence PDF + Cover Sheet. Wait for
          generation to complete, or proceed if you don't need them.
        </div>
      )}
      {!createdPack && interviewPrepDoc.status === 'queued' && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200"
          data-testid="workspace-pack-interview-prep-queued-banner"
        >
          <span className="font-semibold">Interview Prep is queued.</span>{' '}
          The pack will be created without it if you proceed now.
        </div>
      )}
      {!createdPack && interviewPrepDoc.status === 'failed' && (
        <div
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-rose-200"
          data-testid="workspace-pack-interview-prep-failed-banner"
        >
          <span className="font-semibold">Interview Prep generation failed</span>{' '}
          {interviewPrepDoc.errorCode
            ? `(${interviewPrepDoc.errorCode}). `
            : '. '}
          The pack will be created without it. You can retry Interview
          Prep separately and create a fresh pack afterwards.
        </div>
      )}

      {creditsLoading && !creating && (
        <p
          className="text-[11px] text-ndor-ink-muted"
          data-testid="workspace-pack-credits-loading-hint"
        >
          Loading your wallet…
        </p>
      )}
    </div>
  );
}
